import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Save, Settings, Lock, Unlock, EyeOff, Sparkles, RefreshCw, ToggleLeft, ToggleRight, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PLAN_TYPES = [
  { value: 'taster', label: 'Taster' },
  { value: 'growth', label: 'Growth' },
  { value: 'authority', label: 'Authority' },
  { value: 'custom', label: 'Custom' }
];

const PRESETS = {
  taster: { keywords_limit: 8, blogs_limit: 1, backlinks_limit: 100, ai_visibility_enabled: false, account_manager_enabled: false, chat_enabled: false, addons_config: { social_media: 'locked', paid_ads: 'locked', ai_video: 'locked', website_creation: 'locked', product_dev: 'locked', legal_translations: 'enabled' } },
  growth: { keywords_limit: 20, blogs_limit: 3, backlinks_limit: 200, ai_visibility_enabled: true, account_manager_enabled: true, chat_enabled: true, addons_config: { social_media: 'enabled', paid_ads: 'enabled', ai_video: 'enabled', website_creation: 'enabled', product_dev: 'locked', legal_translations: 'enabled' } },
  authority: { keywords_limit: 40, blogs_limit: 8, backlinks_limit: 400, ai_visibility_enabled: true, account_manager_enabled: true, chat_enabled: true, addons_config: { social_media: 'enabled', paid_ads: 'enabled', ai_video: 'enabled', website_creation: 'enabled', product_dev: 'enabled', legal_translations: 'enabled' } }
};

const ADDONS = [
  { key: 'social_media', label: 'Social Media' },
  { key: 'paid_ads', label: 'Paid Ads' },
  { key: 'ai_video', label: 'AI Video' },
  { key: 'website_creation', label: 'Website Creation' },
  { key: 'product_dev', label: 'Product / Software Development' },
  { key: 'legal_translations', label: 'Legal Translations' }
];

const ADDON_STATES = [
  { value: 'enabled', label: 'Enabled', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', Icon: Unlock },
  { value: 'custom', label: 'Custom', cls: 'bg-violet-100 text-violet-700 border-violet-300', Icon: Unlock },
  { value: 'locked', label: 'Locked', cls: 'bg-amber-100 text-amber-700 border-amber-300', Icon: Lock },
  { value: 'disabled', label: 'Disabled', cls: 'bg-slate-100 text-slate-600 border-slate-300', Icon: EyeOff }
];

function Toggle({ value, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`inline-flex items-center transition-colors ${value ? 'text-emerald-600' : 'text-slate-400'}`}
      data-testid={testid}
    >
      {value ? <ToggleRight className="w-9 h-9" /> : <ToggleLeft className="w-9 h-9" />}
    </button>
  );
}

export default function PlanConfigModal({ client, onClose, onSaved }) {
  const initial = client.plan_config || PRESETS.growth;
  const [cfg, setCfg] = useState({ ...PRESETS.growth, ...initial, plan_type: initial.plan_type || 'growth', addons_config: { ...PRESETS.growth.addons_config, ...(initial.addons_config || {}) }, addons_custom: { ...(initial.addons_custom || {}) } });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [manualOverride, setManualOverride] = useState({
    enabled: !!client.manual_override_enabled,
    notes: client.manual_override_notes || '',
    at: client.manual_override_at,
    by: client.manual_override_by
  });

  useEffect(() => {
    setCfg({ ...PRESETS.growth, ...(client.plan_config || {}), plan_type: (client.plan_config?.plan_type) || 'growth', addons_config: { ...PRESETS.growth.addons_config, ...((client.plan_config?.addons_config) || {}) }, addons_custom: { ...((client.plan_config?.addons_custom) || {}) } });
  }, [client]);

  const setPreset = (planType) => {
    if (planType === 'custom') {
      setCfg({ ...cfg, plan_type: 'custom' });
      return;
    }
    setCfg({ plan_type: planType, ...PRESETS[planType], addons_config: { ...PRESETS[planType].addons_config } });
  };

  const updateLimit = (k, v) => {
    const n = parseInt(v || '0', 10);
    if (Number.isNaN(n) || n < 0) return;
    setCfg({ ...cfg, [k]: n });
  };

  const updateAddon = (key, state) => {
    setCfg({ ...cfg, addons_config: { ...cfg.addons_config, [key]: state } });
  };

  const updateAddonCustom = (addonKey, field, value) => {
    const existing = cfg.addons_custom?.[addonKey] || {};
    setCfg({ ...cfg, addons_custom: { ...(cfg.addons_custom || {}), [addonKey]: { ...existing, [field]: value } } });
  };

  // Per-addon custom limit field definitions
  const CUSTOM_FIELDS = {
    social_media: [
      { field: 'posts', label: 'Number of posts', type: 'number' },
      { field: 'reels', label: 'Reels', type: 'number' },
      { field: 'stories', label: 'Stories', type: 'number' },
      { field: 'platforms', label: 'Platforms (comma-separated)', type: 'text' },
    ],
    paid_ads: [
      { field: 'ad_spend_limit', label: 'Ad spend limit', type: 'text' },
      { field: 'platforms', label: 'Platforms (comma-separated)', type: 'text' },
      { field: 'campaign_limit', label: 'Campaign limit', type: 'number' },
    ],
    ai_video: [
      { field: 'videos', label: 'Number of videos', type: 'number' },
    ],
    website_creation: [
      { field: 'pages', label: 'Number of pages', type: 'number' },
      { field: 'package', label: 'Website package override', type: 'text' },
    ],
    product_dev: [
      { field: 'project_tier', label: 'Project tier', type: 'text' },
      { field: 'development_hours', label: 'Development hours', type: 'number' },
      { field: 'special_scope', label: 'Special scope', type: 'text' },
    ],
    legal_translations: [
      { field: 'special_rate', label: 'Special rate / agreement', type: 'text' },
    ],
  };

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/firms/${client.firm_id}/plan`, cfg, { headers: auth() });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="plan-config-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-xs text-slate-500">Plan & Access Configuration</div>
              <h3 className="text-lg font-bold text-slate-900">{client.firm_name}</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="plan-modal-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Plan type selector */}
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Plan Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              {PLAN_TYPES.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPreset(p.value)}
                  className={`px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all ${cfg.plan_type === p.value ? 'border-violet-500 bg-violet-50 text-slate-900 shadow-sm' : 'border-slate-200 hover:border-slate-300 text-slate-700'}`}
                  data-testid={`plan-type-${p.value}`}
                >
                  {p.value === 'custom' && <Sparkles className="w-3.5 h-3.5 inline mr-1 text-violet-500" />}
                  {p.label}
                </button>
              ))}
            </div>
            {cfg.plan_type !== 'custom' && (
              <button
                onClick={() => setCfg({ plan_type: cfg.plan_type, ...PRESETS[cfg.plan_type], addons_config: { ...PRESETS[cfg.plan_type].addons_config } })}
                className="text-xs text-violet-600 hover:text-violet-700 mt-2 inline-flex items-center gap-1"
                data-testid="plan-reset-preset"
              >
                <RefreshCw className="w-3 h-3" /> Reset to {cfg.plan_type} preset
              </button>
            )}
          </div>

          {/* Core limits */}
          <div className="bg-slate-50 rounded-2xl p-5">
            <h4 className="text-sm font-bold text-slate-900 mb-3">Core Limits</h4>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-600">Keywords</label>
                <input type="number" min="0" value={cfg.keywords_limit} onChange={(e) => updateLimit('keywords_limit', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="plan-keywords-limit" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Blogs / month</label>
                <input type="number" min="0" value={cfg.blogs_limit} onChange={(e) => updateLimit('blogs_limit', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="plan-blogs-limit" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Backlinks / month</label>
                <input type="number" min="0" value={cfg.backlinks_limit} onChange={(e) => updateLimit('backlinks_limit', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="plan-backlinks-limit" />
              </div>
            </div>
          </div>

          {/* Feature toggles */}
          <div className="bg-slate-50 rounded-2xl p-5">
            <h4 className="text-sm font-bold text-slate-900 mb-3">Feature Toggles</h4>
            <div className="space-y-2">
              {[
                { key: 'ai_visibility_enabled', label: 'AI Visibility Tracking' },
                { key: 'account_manager_enabled', label: 'Dedicated Account Manager' },
                { key: 'chat_enabled', label: 'Chat with Account Manager' }
              ].map(t => (
                <div key={t.key} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3">
                  <span className="text-sm font-medium text-slate-800">{t.label}</span>
                  <Toggle value={!!cfg[t.key]} onChange={(v) => setCfg({ ...cfg, [t.key]: v })} testid={`plan-toggle-${t.key}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Addons */}
          <div className="bg-slate-50 rounded-2xl p-5">
            <h4 className="text-sm font-bold text-slate-900 mb-3">Add-ons Access</h4>
            <div className="space-y-2">
              {ADDONS.map(a => {
                const state = cfg.addons_config[a.key];
                const showCustomFields = state === 'custom' || state === 'enabled';
                const customVals = cfg.addons_custom?.[a.key] || {};
                return (
                <div key={a.key} className="bg-white border border-slate-200 rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">{a.label}</span>
                    <div className="flex gap-1 flex-wrap">
                      {ADDON_STATES.map(s => {
                        const active = cfg.addons_config[a.key] === s.value;
                        return (
                          <button
                            key={s.value}
                            onClick={() => updateAddon(a.key, s.value)}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${active ? s.cls : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
                            data-testid={`plan-addon-${a.key}-${s.value}`}
                          >
                            <s.Icon className="w-3 h-3" />
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {showCustomFields && CUSTOM_FIELDS[a.key] && (
                    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`plan-addon-custom-${a.key}`}>
                      <div className="text-[11px] font-semibold text-slate-500 mb-2">
                        {state === 'enabled'
                          ? 'Optional allowances shown to the client (leave blank to hide)'
                          : 'Custom allowances shown to the client'}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {CUSTOM_FIELDS[a.key].map((f) => (
                        <div key={f.field}>
                          <label className="text-[11px] text-slate-500">{f.label}</label>
                          <input
                            type={f.type}
                            value={customVals[f.field] ?? ''}
                            onChange={(e) => updateAddonCustom(a.key, f.field, f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                            className="mt-0.5 w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs"
                            data-testid={`plan-addon-custom-${a.key}-${f.field}`}
                          />
                        </div>
                      ))}
                      </div>
                    </div>
                  )}
                </div>
              );})}
            </div>
          </div>

          {error && <div className="px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-sm" data-testid="plan-error">{error}</div>}

          {/* Manual override Stripe billing rules + billing metadata */}
          <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-4" data-testid="manual-override-section">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-4 h-4 text-violet-600" />
                  <span className="text-sm font-semibold text-slate-900">Manual Override Stripe Billing Rules</span>
                </div>
                <p className="text-xs text-slate-600 max-w-lg">
                  When enabled, this client's dashboard access is controlled manually by Super Admin and
                  will <strong>not</strong> be restricted by Stripe subscription status.
                </p>
                {client.manual_override_enabled && (
                  <p className="text-[11px] text-violet-700 mt-2">
                    Active - set {client.manual_override_at ? new Date(client.manual_override_at).toLocaleDateString() : '-'}
                    {client.manual_override_by ? ` by ${client.manual_override_by}` : ''}
                    {client.manual_override_notes ? ` · ${client.manual_override_notes}` : ''}
                  </p>
                )}
              </div>
              <Toggle
                value={!!manualOverride.enabled}
                onChange={async (v) => {
                  try {
                    const notes = v ? (window.prompt('Optional note for this override:', manualOverride.notes || '') || '') : '';
                    const res = await axios.patch(
                      `${API}/admin/clients/${client.firm_id}/manual-override`,
                      { enabled: v, notes },
                      { headers: auth() }
                    );
                    setManualOverride({
                      enabled: !!res.data.firm?.manual_override_enabled,
                      notes: res.data.firm?.manual_override_notes || '',
                      at: res.data.firm?.manual_override_at,
                      by: res.data.firm?.manual_override_by
                    });
                    onSaved?.();
                  } catch (e) {
                    setError(e.response?.data?.detail || 'Failed to update manual override');
                  }
                }}
                testid="plan-manual-override-toggle"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 pt-4 border-t border-violet-200/60">
              <BillingField label="Billing status" value={client.billing_status || '-'} />
              <BillingField label="Currency" value={(client.billing_currency || '-').toUpperCase()} />
              <BillingField label="Interval" value={client.billing_interval || '-'} />
              <BillingField label="Stripe customer" value={client.stripe_customer_id || '-'} mono />
              <BillingField label="Stripe subscription" value={client.stripe_subscription_id || '-'} mono />
              <BillingField label="Stripe price" value={client.stripe_price_id || '-'} mono />
              <BillingField label="Current period end" value={client.current_period_end ? new Date(client.current_period_end).toLocaleDateString() : '-'} />
              <BillingField label="Last payment" value={client.last_payment_at ? new Date(client.last_payment_at).toLocaleDateString() : '-'} />
              <BillingField label="Cancelled at" value={client.subscription_cancelled_at ? new Date(client.subscription_cancelled_at).toLocaleDateString() : '-'} />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium" data-testid="plan-cancel">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg shadow-violet-500/30 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="plan-save">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Plan & Access'}
          </button>
        </div>
      </div>
    </div>
  );
}


function BillingField({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className={`text-xs text-slate-800 ${mono ? 'font-mono' : 'font-medium'} truncate`} title={value}>{value}</div>
    </div>
  );
}
